import {
  Anchor,
  Avatar,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  ThemeIcon,
  Title,
  Tabs,
  Text,
  Button,
  Popover,
  TextInput
} from "@mantine/core";
import {  DatePicker } from '@mantine/dates';
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import parse from "html-react-parser";
import fetchApi from "../../Lib/api";
import {
  IconArrowLeft,
  IconSocial,
  IconChevronRight,
  IconChevronDown,
} from "@tabler/icons-react";
import { Users } from "./Members";
import useUserStore from "../../../stores/userStore";
import { useQuery } from "@tanstack/react-query";
import StartupCards from "../../../stories/StartupCards";
import { registerLicense } from "@syncfusion/ej2-base";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

registerLicense(
  "ORg4AjUWIQA/Gnt2XVhhQlJHfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5RdEBjUXpYcndVRmlc"
);



const useActivities = (startDate,endDate) => {
  const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
  const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
  const url=`/activities/index?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
  return useQuery({
    queryKey: [`activities-${startDate}-${endDate}`], // Unique query key for caching
    queryFn: async () => {
      const response = await fetchApi(url, 'GET');

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch activities");
      }

      return await response.json(); // Return the parsed JSON data
    },
    retry: 1, // Retry once on failure
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Cache inactive data for 30 minutes
  });
};

export default function Activity() {
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [view, setView] = useState("activity");
  const setUser = useUserStore((state) => state.setUser);
  const today = dayjs().toDate();
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "month").toDate());
  const [endDate, setEndDate] = useState(today);
  const [selectedTab, setSelectedTab] = useState("1-month");
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const { data, isLoading, isError, error } = useActivities(startDate,endDate);
  const {t}=useTranslation();
  useEffect(() => {
    if (data) {
      setStats(data.counts || []);
      setActivity(data.activities || []);
      setUser((prevUser) => ({
        ...prevUser,
        recent_activities_count: 0, // Reset the count
      }));
    }
  }, [data, setStats, setActivity, setUser]);

  if (isLoading) {
    return (
      <Loader
        className="mx-auto mt-10"
        color="indigo"
        size="lg"
        variant="dots"
      />
    );
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }
  const handleTabChange = (value) => {
    setSelectedTab(value);
    const now = dayjs();

    switch (value) {
      case "1-month":
        setStartDate(now.subtract(1, "month").toDate());
        setEndDate(today);
        break;
      case "2-month":
        setStartDate(now.subtract(2, "month").toDate());
        setEndDate(today);
        break;
      case "3-month":
        setStartDate(now.subtract(3, "month").toDate());
        setEndDate(today);
        break;
      case "custom":
        setEndOpen(true);
        break;
      default:
        break;
    }
  };
  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date > endDate) {
      setEndDate(date); // Auto-update end date if it's behind start date
    }
  };

  const handleEndDateChange = (date) => {
    if (date >= startDate) {
      setEndDate(date);
    } 
  };

  return (
    <section className="flex justify-center ">
      <div className="max-w-7xl w-full p-3 lg:p-6">
        <Stack spacing={20}>
          <Group>
            <ThemeIcon
              variant="gradient"
              size="lg"
              aria-label="Gradient action icon"
              gradient={{ from: "blue", to: "rgba(8, 0, 18, 1)", deg: 90 }}
            >
              <IconSocial size={20} />
            </ThemeIcon>
            <Title order={3} className="text-gray-600">
             {t("activity.header")}
            </Title>
          </Group>
          <div>
            <Group className="flex-row flex-nowrap overflow-x-auto  lg:overflow-visible">
              <div
                onClick={() => setView("followers")}
                className="bg-white hover:bg-gray-100 overflow-hidden shadow sm:rounded-lg w-[180px] flex-shrink-0 lg:w-1/3 cursor-pointer mr-3 last:mr-0 lg:mr-0"
              >
                <div className="px-4 py-5 sm:p-6">
                  <dl>
                    <dt className="text-sm leading-5 font-medium text-gray-500 truncate">
                     {t("activity.followers")}
                    </dt>
                    <div className="flex justify-between items-center">
                      <dd className="mt-1 text-3xl leading-9 font-semibold text-indigo-600">
                        {stats.followers_count || 0}
                      </dd>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="hidden lg:block w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                        />
                      </svg>
                    </div>
                  </dl>
                </div>
              </div>
              <div
                onClick={() => setView("followings")}
                className="bg-white hover:bg-gray-100 overflow-hidden shadow sm:rounded-lg w-[180px] flex-shrink-0 lg:w-1/3 cursor-pointer mr-3 last:mr-0 lg:mr-0"
              >
                <div className="px-4 py-5 sm:p-6">
                  <dl>
                    <dt className="text-sm leading-5 font-medium text-gray-500 truncate">
                    {t("activity.following")}
                    </dt>

                    <div className="flex justify-between items-center">
                      <dd className="mt-1 text-3xl leading-9 font-semibold text-indigo-600">
                        {stats.followings_count || 0}
                      </dd>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="hidden lg:block w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                        />
                      </svg>
                    </div>
                  </dl>
                </div>
              </div>
              <div
                onClick={() => setView("startups")}
                className="bg-white hover:bg-gray-100 overflow-hidden shadow sm:rounded-lg w-[180px] flex-shrink-0 lg:w-1/3 cursor-pointer mr-3 last:mr-0 lg:mr-0"
              >
                <div className="px-4 py-5 sm:p-6">
                  <dl>
                    <dt className="text-sm leading-5 font-medium text-gray-500 truncate">
                  {t("activity.startups_following")}
                    </dt>

                    <div className="flex justify-between items-center">
                      <dd className="mt-1 text-3xl leading-9 font-semibold text-indigo-600">
                        {stats.followed_startups_count || 0}
                      </dd>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="hidden lg:block w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                        />
                      </svg>
                    </div>
                  </dl>
                </div>
              </div>
            </Group>
          </div>
      <Tabs value={selectedTab} onTabChange={handleTabChange} variant="pills" color="violet" >
        <Tabs.List justify="space-between">
          <Tabs.Tab value="1-month">{t("activity.duration.one")}</Tabs.Tab>
          <Tabs.Tab value="2-month">{t("activity.duration.two")}</Tabs.Tab>
          <Tabs.Tab value="3-month">{t("activity.duration.three")}</Tabs.Tab>
          <Tabs.Tab value="custom">{t("activity.duration.custom")}</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      {selectedTab === "custom" && (
        <Group mt="md">
          {/* Start Date Picker */}
          <Popover
            opened={startOpen}
            onChange={setStartOpen}
            position="bottom"
            withArrow
          >
            <Popover.Target>
              <TextInput
                value={dayjs(startDate).format("YYYY-MM-DD")}
                onClick={() => setStartOpen((o) => !o)}
                label="Start Date"
                readOnly
              />
            </Popover.Target>
            <Popover.Dropdown>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={new Date()}
              />
            </Popover.Dropdown>
          </Popover>

          {/* End Date Picker */}
          <Popover
            opened={endOpen}
            onChange={setEndOpen}
            position="bottom"
            withArrow
          >
            <Popover.Target>
              <TextInput
                value={dayjs(endDate).format("YYYY-MM-DD")}
                onClick={() => setEndOpen((o) => !o)}
                label="End Date"
                readOnly
              />
            </Popover.Target>
            <Popover.Dropdown>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                maxDate={new Date()}
              />
            </Popover.Dropdown>
          </Popover>
          <Button className="mt-3" variant="outline" color="red" onClick={() => setStartDate(dayjs().subtract(1, "month").toDate()) & setEndDate(today)}>
            Clear Dates
          </Button>
          </Group>
      )}
          {view === "activity" && (
            <>
              {activity.map((ac) => (
                <ActivityCard ac={ac} />
              ))}
            </>
          )}
          {view === "followers" && <Followers />}

          {view === "followings" && <Followings />}

          {view === "startups" && <Startups />}
        </Stack>
      </div>
    </section>
  );
}

const Followers = ({}) => {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchApi(`/api/users/followers`, "GET");

        if (response.ok) {
          const res = await response.json();
          console.log(res);
          setUsers(res);
        } else {
          const msg = (await response.json()).error;
          console.error(msg);
        }
      } catch (error) {
        console.error("Error during fetching activities:", error);
      }
    };

    fetchData();
  }, []);
  return (
    <Stack spacing={12}>
      <Title order={4} className="text-gray-600">
        People You are Following
      </Title>
      <Users users={users} setUsers={setUsers} />
    </Stack>
  );
};

const Followings = ({}) => {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchApi(`/api/users/followings`, "GET");

        if (response.ok) {
          const res = await response.json();
          console.log(res);
          setUsers(res);
        } else {
          const msg = (await response.json()).error;
          console.error(msg);
        }
      } catch (error) {
        console.error("Error during fetching activities:", error);
      }
    };

    fetchData();
  }, []);
  return (
    <Stack spacing={12}>
      <Title order={4} className="text-gray-600">
        People Following You
      </Title>
      <Users users={users} setUsers={setUsers} />
    </Stack>
  );
};

const Startups = ({}) => {
  const [startups, setStartups] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchApi(`/api/users/followed_startups`, "GET");

        if (response.ok) {
          const res = await response.json();
          console.log(res);
          setStartups(res);
        } else {
          const msg = (await response.json()).error;
          console.error(msg);
        }
      } catch (error) {
        console.error("Error during fetching activities:", error);
      }
    };

    fetchData();
  }, []);
  return (
    <Stack spacing={12}>
      <Title order={4} className="text-slate-900">
        Startups You Are Following
      </Title>

      <StartupCards 
        allCards={startups.map(({ startup_entity, openings_length, members }) => ({
          id: startup_entity.id,
          name: startup_entity.name,
          description: startup_entity.description,
          image_url: startup_entity.image_url || (startup_entity.image && startup_entity.image.url),
          slug: startup_entity.slug,
          entity: startup_entity.entity_type,
          industry: startup_entity.industry,
          project_type: startup_entity.project_type,
          country: startup_entity.country,
          city: startup_entity.city,
          openings_length: openings_length,
          open_fo_application: startup_entity.open_fo_application,
          open_fo_applications: startup_entity.open_fo_applications,
          is_owner: startup_entity.is_owner,
          has_applied: startup_entity.has_applied,
          members: members
        }))}
      />
    </Stack>
  );
};

const ActivityCard = ({ ac }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user: {id: currentUserId} } = useUserStore((state) => state.user);
  
  // Function to replace actor's name with "You" if the actor is the current user
  const getPersonalizedDescription = (activityItem) => {
    if (!activityItem.description && !activityItem.message) return "";
    
    const content = activityItem.description || activityItem.message;
    
    // Check if the current user is the actor
    if (activityItem.actor && activityItem.actor.id === currentUserId) {
      // Replace actor's first name with "You" at the beginning of the string
      // This uses a regex to match the name only at the start of the string
      const namePattern = new RegExp(`^${activityItem.actor.first_name}\\b`, 'i');
      return content.replace(namePattern, "You");
    }
    
    return content;
  };
  
  return (
    <div
      key={ac.date}
      className="p-5 mb-4 border shadow border-gray-100 rounded-lg bg-white"
    >
      <div className="flex justify-between">
        <time className="text-lg font-semibold text-gray-900 ">{ac.date}</time>
        <div
          className="cursor-pointer"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? <IconChevronDown /> : <IconChevronRight />}
        </div>
      </div>

      <ol className="mt-3 divide-y divider-gray-200">
        {(isExpanded ? ac.activities : ac.activities.slice(0, 4)).map(
          (activity) => {
            const { id, actor, sub_message, view, url } = activity;
            const personalizedContent = getPersonalizedDescription(activity);
            
            return (
              <li className="list-none" key={id}>
                <a
                  href={url || "#"}
                  className="items-center block p-3 sm:flex hover:bg-gray-100 "
                >
                  <Avatar
                    className="w-12 h-12 mb-3 me-3 rounded-full sm:mb-0"
                    src={
                      actor.avatar_url ||
                      "https://placehold.co/600x400?text=No Image"
                    }
                    alt={actor.name}
                  />
                  <div className="text-gray-600 ">
                    <div className="text-base font-normal">
                      {parse(personalizedContent)}
                    </div>
                    {sub_message && (
                      <div className="text-sm font-normal">{sub_message}</div>
                    )}
                    {view === "Private" ? (
                      <span className="inline-flex items-center text-xs font-normal text-gray-700 ">
                        <svg
                          className="w-2.5 h-2.5 me-1"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="m2 13.587 3.055-3.055A4.913 4.913 0 0 1 5 10a5.006 5.006 0 0 1 5-5c.178.008.356.026.532.054l1.744-1.744A8.973 8.973 0 0 0 10 3C4.612 3 0 8.336 0 10a6.49 6.49 0 0 0 2 3.587Z" />
                          <path d="m12.7 8.714 6.007-6.007a1 1 0 1 0-1.414-1.414L11.286 7.3a2.98 2.98 0 0 0-.588-.21l-.035-.01a2.981 2.981 0 0 0-3.584 3.583c0 .012.008.022.01.033.05.204.12.401.211.59l-6.007 6.007a1 1 0 1 0 1.414 1.414L8.714 12.7c.189.091.386.162.59.211.011 0 .021.007.033.01a2.981 2.981 0 0 0 3.584-3.584c0-.012-.008-.023-.011-.035a3.05 3.05 0 0 0-.21-.588Z" />
                          <path d="M17.821 6.593 14.964 9.45a4.952 4.952 0 0 1-5.514 5.514L7.665 16.75c.767.165 1.55.25 2.335.251 6.453 0 10-5.258 10-7 0-1.166-1.637-2.874-2.179-3.407Z" />
                        </svg>
                        Private
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-normal text-gray-500 dark:text-gray-400">
                        <svg
                          className="w-2.5 h-2.5 me-1"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 .5a9.5 9.5 0 1 0 0 19 9.5 9.5 0 0 0 0-19ZM8.374 17.4a7.6 7.6 0 0 1-5.9-7.4c0-.83.137-1.655.406-2.441l.239.019a3.887 3.887 0 0 1 2.082 2.5 4.1 4.1 0 0 0 2.441 2.8c1.148.522 1.389 2.007.732 4.522Zm3.6-8.829a.997.997 0 0 0-.027-.225 5.456 5.456 0 0 0-2.811-3.662c-.832-.527-1.347-.854-1.486-1.89a7.584 7.584 0 0 1 8.364 2.47c-1.387.208-2.14 2.237-2.14 3.307a1.187 1.187 0 0 1-1.9 0Zm1.626 8.053-.671-2.013a1.9 1.9 0 0 1 1.771-1.757l2.032.619a7.553 7.553 0 0 1-3.132 3.151Z" />
                        </svg>
                        Public
                      </span>
                    )}
                  </div>
                </a>
              </li>
            );
          }
        )}
      </ol>
    </div>
  );
};